import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_LOCALE, resolveSupported } from 'i18n/locales';

// The active UI language (D5): user- and device-scoped, persisted to
// localStorage. Structurally the twin of useDecimalConventionStore, including
// the two load-bearing details — a `cg.*` persist key, and a non-React getter
// for code that runs outside a component.
//
// `locale` is null until either the user picks one or `initFromBrowser`
// resolves a default. The initializer is a no-op once a value exists, so a
// reload or an org switch can never clobber an explicit choice.
//
// `locale` is the EFFECTIVE language every consumer reads. `preferredLocale` is
// the user's own choice, which the active organization can temporarily override
// via `pinLocale` (operators/consultants are English-only for now — see
// modules/Common/config/languageAccess). Keeping the two apart is what makes the
// pin non-destructive: a user whose device is set to German and who steps into a
// pinned org gets English there and their German back everywhere else, instead
// of having the choice silently reset.
export const useLocaleStore = create(
  persist(
    (set, get) => ({
      locale: null,
      preferredLocale: null,
      setLocale: (locale) => set({ locale, preferredLocale: locale }),
      initFromBrowser: () => {
        if (get().locale) return;
        const locale = detectBrowserLocale() ?? DEFAULT_LOCALE;
        set({ locale, preferredLocale: locale });
      },
      // Pin the effective language to `code`, or pass null to lift the pin and
      // restore the preference. Backfills `preferredLocale` from `locale` on the
      // way in, so a device that predates this field doesn't lose its choice the
      // first time a pin applies. Callers must load the target locale's
      // resources (applyLocale) BEFORE committing here — see the pickers.
      pinLocale: (code) =>
        set((state) => {
          const preference = state.preferredLocale ?? state.locale;
          return { locale: code ?? preference ?? state.locale, preferredLocale: preference };
        }),
    }),
    {
      name: 'cg.locale',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

// navigator.language is read HERE AND NOWHERE ELSE (an invariant in
// 00-OVERVIEW.md). Walks the browser's ordered preference list and returns the
// first entry we actually ship — not the first entry, which is frequently a
// language we have no resources for.
function detectBrowserLocale() {
  if (typeof navigator === 'undefined') return null;
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  return tags.map(resolveSupported).find(Boolean) ?? null;
}

// Non-React accessor. Phase 2's formatters, phase 7's export utilities and the
// axios request interceptor all call this — they run outside React and must
// not need a hook.
export const getLocale = () => useLocaleStore.getState().locale ?? DEFAULT_LOCALE;

// The user's own choice, ignoring any org-level pin. Only the pin machinery
// needs this — everything that renders or formats reads getLocale().
export const getPreferredLocale = () => {
  const { locale, preferredLocale } = useLocaleStore.getState();
  return preferredLocale ?? locale ?? DEFAULT_LOCALE;
};
